import { useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Grid, Typography } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";

import MainCard from "components/MainCard";
import DatePickerElement from "components/DatePickerElement";
import { SelectField } from "components/@extended/SelectField";

import useTopBooksPerformanceStore from "../store/TopBooksPerformanceStore";
import { DateRangePreset } from "../api/types";

interface IPresetOption {
  id: number;
  name: string;
  value: DateRangePreset;
}

const PRESET_OPTIONS: IPresetOption[] = [
  { id: 1, name: "Today", value: "today" },
  { id: 2, name: "This Week", value: "thisWeek" },
  { id: 3, name: "This Month", value: "thisMonth" },
  { id: 4, name: "This Year", value: "thisYear" },
  { id: 5, name: "Custom Range", value: "custom" },
];

const presetIdToValue = (id: number | null | undefined): DateRangePreset =>
  PRESET_OPTIONS.find((option) => option.id === id)?.value ?? "thisYear";

const presetValueToId = (value: DateRangePreset): number =>
  PRESET_OPTIONS.find((option) => option.value === value)?.id ?? 4;

interface IDateRangeFilterFormValues {
  presetId: number;
  customFromDate: Dayjs | null;
  customToDate: Dayjs | null;
}

const DateRangePresetFilter = () => {
  const {
    dateRangePreset,
    customFromDate,
    customToDate,
    setDateRangePreset,
    setCustomDateRange,
  } = useTopBooksPerformanceStore();

  const { control, trigger } = useForm<IDateRangeFilterFormValues>({
    mode: "all",
    defaultValues: {
      presetId: presetValueToId(dateRangePreset),
      customFromDate: customFromDate ? dayjs(customFromDate) : null,
      customToDate: customToDate ? dayjs(customToDate) : null,
    },
  });

  const presetId = useWatch({ control, name: "presetId" });
  const watchedFromDate = useWatch({ control, name: "customFromDate" });
  const watchedToDate = useWatch({ control, name: "customToDate" });

  const preset = useMemo(() => presetIdToValue(presetId), [presetId]);

  useEffect(() => {
    setDateRangePreset(preset);
  }, [preset, setDateRangePreset]);


  useEffect(() => {
    if (preset === "custom" && watchedToDate) {
      trigger("customToDate");
    }
  }, [watchedFromDate, preset, trigger, watchedToDate]);

  useEffect(() => {
    if (preset === "custom" && watchedFromDate) {
      trigger("customFromDate");
    }
  }, [watchedToDate, preset, trigger, watchedFromDate]);

  useEffect(() => {
    if (preset === "custom") {
      const isFromValid = watchedFromDate && dayjs(watchedFromDate).isValid();
      const isToValid = watchedToDate && dayjs(watchedToDate).isValid();

      if (
        isFromValid &&
        isToValid &&
        !dayjs(watchedFromDate).isAfter(dayjs(watchedToDate), "day")
      ) {
        setCustomDateRange(
          watchedFromDate.toString(),
          watchedToDate.toString(),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, watchedFromDate, watchedToDate]);

  return (
    <MainCard>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm="auto">
          <Typography variant="subtitle1" sx={{ whiteSpace: "nowrap" }}>
            Date Range:
          </Typography>
        </Grid>
        <Grid item xs={12} sm={3}>
          <SelectField
            control={control}
            name="presetId"
            disableClearable
            options={PRESET_OPTIONS}
            placeholder="Select Date Range"
          />
        </Grid>
        {preset === "custom" && (
          <>
            <Grid item xs={12} sm={3}>
              <DatePickerElement
                control={control}
                name="customFromDate"
                label="From Date *"
                format="DD-MM-YYYY"
                sx={{ width: "100%" }}
                maxDate={
                  watchedToDate && dayjs(watchedToDate).isValid()
                    ? dayjs(watchedToDate)
                    : undefined
                }
                rules={{
                  required:
                    preset === "custom" ? "From Date is required" : false,
                  validate: {
                    validDate: (value: Dayjs | null) => {
                      if (preset !== "custom") return true;
                      if (!value) return "From Date is required";
                      return dayjs(value).isValid() || "Invalid Date";
                    },
                    beforeToDate: (value: Dayjs | null) => {
                      if (preset !== "custom" || !value || !watchedToDate)
                        return true;
                      if (
                        dayjs(value).isValid() &&
                        dayjs(watchedToDate).isValid()
                      ) {
                        return (
                          !dayjs(value).isAfter(dayjs(watchedToDate), "day") ||
                          "From Date cannot be after To Date"
                        );
                      }
                      return true;
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DatePickerElement
                control={control}
                name="customToDate"
                label="To Date *"
                format="DD-MM-YYYY"
                sx={{ width: "100%" }}
                minDate={
                  watchedFromDate && dayjs(watchedFromDate).isValid()
                    ? dayjs(watchedFromDate)
                    : undefined
                }
                rules={{
                  required: preset === "custom" ? "To Date is required" : false,
                  validate: {
                    validDate: (value: Dayjs | null) => {
                      if (preset !== "custom") return true;
                      if (!value) return "To Date is required";
                      return dayjs(value).isValid() || "Invalid Date";
                    },
                    afterFromDate: (value: Dayjs | null) => {
                      if (preset !== "custom" || !value || !watchedFromDate)
                        return true;
                      if (
                        dayjs(value).isValid() &&
                        dayjs(watchedFromDate).isValid()
                      ) {
                        return (
                          !dayjs(value).isBefore(
                            dayjs(watchedFromDate),
                            "day",
                          ) || "To Date cannot be before From Date"
                        );
                      }
                      return true;
                    },
                  },
                }}
              />
            </Grid>
          </>
        )}
      </Grid>
    </MainCard>
  );
};

export default DateRangePresetFilter;
